import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Email as EmailIcon,
  Email as EmailNotificationIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import type {
  Chat,
  ConversationMessage,
  MessageCategory,
  ProjectInfo,
  StreamInfo,
} from '@/types/chat';
import { getCategoryLabel, getCategoryColor } from '@/constants/conversationCategoryCatalog';
import { MessageInput } from './MessageInput';
import { DraftReplyBlock } from './DraftReplyBlock';
import { INBOX_SEND_DELAY_SECONDS, type InboxPendingSendState } from './inboxSendDelay';
import { useNavigate } from 'react-router-dom';
import { chatApi } from '@/api/chat';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ScheduledMessage } from '@/types/chat';
import { useAuth } from '@/hooks/useAuth';

interface ConversationViewProps {
  selectedChat: Chat | null;
  onSendMessage: (message: string, chatId?: string) => void;
  onScheduleMessage?: (message: string, scheduledAt: Date) => void;
  isSending: boolean;
  onMarkImportant?: (chatId: string, isImportant: boolean) => void;
  onMarkArchived?: (chatId: string, isArchived: boolean) => void;
  onMarkUnread?: (chatId: string) => void;
  onNavigateToDetails?: () => void;
  onSendEmailNotification?: () => void;
  isAdmin?: boolean;
  onChatUpdate?: (chat: Chat) => void;
  onSendScheduledNowSuccess?: () => void;
  onSendScheduledNowError?: (error: Error) => void;
  owners?: { uuid: string; name: string }[];
}

// Tag component for displaying project/stream and message categories
const Tag: React.FC<{
  label: string;
  color?: string;
  variant?: 'outlined' | 'filled';
  size?: 'small' | 'medium';
  sx?: any;
}> = ({ label, color, variant = 'outlined', size = 'small', sx }) => (
  <Chip
    label={label}
    size={size}
    variant={variant}
    sx={{
      backgroundColor: variant === 'filled' ? color : 'transparent',
      color: variant === 'filled' ? 'white' : color,
      borderColor: color,
      border: variant === 'outlined' ? `1px solid ${color}` : 'none',
      fontSize: '0.7rem',
      height: '20px',
      '& .MuiChip-label': {
        px: 1,
      },
      ...sx,
    }}
  />
);

// Project/Stream combined tag
const ProjectStreamTag: React.FC<{
  project?: ProjectInfo | null;
  stream?: StreamInfo | null;
}> = ({ project, stream }) => {
  if (!project && !stream) return null;
  
  let label = project && stream 
    ? `${project.name} / ${stream.name}`
    : (project?.name || stream?.name || '');
  
  // Limit to 50 characters and add ellipsis if needed
  if (label.length > 50) {
    label = label.substring(0, 47) + '...';
  }
  
  return (
    <Tag
      label={label}
      color="#1976d2"
      variant="outlined"
      sx={{
        maxWidth: '200px',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    />
  );
};

// Message categories tags
const MessageCategoriesTags: React.FC<{
  categories: MessageCategory[];
}> = ({ categories }) => {
  if (!categories || categories.length === 0) return null;
  
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {categories.map((category) => (
        <Tag
          key={category.key}
          label={category.label}
          color={category.color}
          variant="filled"
        />
      ))}
    </Box>
  );
};

// Tags component with edit capability
const TagsDisplay: React.FC<{
  tags: string[];
  chatId: string;
  isAdmin: boolean;
  onUpdate: (tags: string[]) => void;
}> = ({ tags, chatId, isAdmin, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTags, setEditTags] = useState<string[]>(tags || []);
  const queryClient = useQueryClient();

  // Fetch all available tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: chatApi.getTags,
  });

  // Update tags mutation
  const updateTagsMutation = useMutation({
    mutationFn: (newTags: string[]) => chatApi.updateTags(chatId, newTags),
    onSuccess: (updatedChat) => {
      onUpdate(updatedChat.tags || []);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['chat', chatId] });
    },
  });

  // Create tag mutation (admin only)
  const createTagMutation = useMutation({
    mutationFn: (title: string) => chatApi.createTag(title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  useEffect(() => {
    setEditTags(tags || []);
  }, [tags]);

  const handleSave = () => {
    updateTagsMutation.mutate(editTags);
  };

  const handleCancel = () => {
    setEditTags(tags || []);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Autocomplete
          multiple
          freeSolo={isAdmin}
          size="small"
          options={allTags.map(t => t.title)}
          value={editTags}
          onChange={async (_, newValue) => {
            // Filter out non-string values and handle new tags
            const stringValues = newValue.filter((v): v is string => typeof v === 'string');
            
            // If admin added a new tag that doesn't exist, create it
            if (isAdmin) {
              const newTags = stringValues.filter(tag => !allTags.find(t => t.title === tag));
              for (const newTag of newTags) {
                try {
                  await createTagMutation.mutateAsync(newTag);
                } catch (error) {
                  // Tag might already exist, ignore
                }
              }
            }
            
            setEditTags(stringValues);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={isAdmin ? "Add or create tags" : "Add tags"}
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            ))
          }
        />
        <IconButton
          size="small"
          onClick={handleSave}
          disabled={updateTagsMutation.isPending}
          color="primary"
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCancel}
          disabled={updateTagsMutation.isPending}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  if (!tags || tags.length === 0) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          No tags
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsEditing(true)}
          sx={{ width: 20, height: 20 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {tags.map((tag) => (
        <Tag
          key={tag}
          label={tag}
          color="#9C27B0"
          variant="outlined"
        />
      ))}
      <IconButton
        size="small"
        onClick={() => setIsEditing(true)}
        sx={{ width: 20, height: 20 }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const MessageBubble: React.FC<{
  message: ConversationMessage;
  isOwn: boolean;
  senderLabel: string;
}> = ({ message, isOwn, senderLabel }) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '70%',
          bgcolor: isOwn ? 'primary.main' : 'grey.100',
          color: isOwn ? 'white' : 'text.primary',
          borderRadius: 2,
          p: 2,
          position: 'relative',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.5,
            color: isOwn ? 'rgba(255,255,255,0.8)' : 'text.secondary',
            fontWeight: 600,
          }}
        >
          {senderLabel}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {message.text}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: isOwn ? 'rgba(255,255,255,0.7)' : 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {formatTime(message.received_date)}
        </Typography>
      </Box>
    </Box>
  );
};

const ScheduledMessageItem: React.FC<{
  item: ScheduledMessage;
  chatId: string;
  onDelete: () => void;
  onSendNow: () => void;
  isDeleting: boolean;
  isSendingNow: boolean;
}> = ({ item, onDelete, onSendNow, isDeleting, isSendingNow }) => {
  const isFailed = item.status === 'FAILED';
  const scheduledDate = item.scheduled_at ? new Date(item.scheduled_at) : null;
  const formatted = scheduledDate
    ? scheduledDate.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const isBusy = isDeleting || isSendingNow;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isFailed ? 'error.main' : 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {isFailed && (
            <Tooltip title="Failed to send">
              <EventBusyIcon sx={{ fontSize: 18, color: 'error.main' }} />
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {item.text}
          </Typography>
        </Box>
        {formatted && (
          <Typography variant="caption" color="text.secondary">
            {formatted}
          </Typography>
        )}
        {isFailed && item.failed_reason && (
          <Tooltip title={item.failed_reason}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: 'error.dark',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.failed_reason}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Tooltip title="Send now">
        <span>
          <IconButton
            size="small"
            onClick={onSendNow}
            disabled={isBusy}
            color="primary"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete scheduled message">
        <span>
          <IconButton
            size="small"
            onClick={onDelete}
            disabled={isBusy}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const DateSeparator: React.FC<{ date: string }> = ({ date }) => (
  <Box sx={{ textAlign: 'center', my: 2 }}>
    <Typography
      variant="caption"
      sx={{
        bgcolor: 'grey.100',
        px: 2,
        py: 0.5,
        borderRadius: 1,
        color: 'text.secondary',
      }}
    >
      {new Date(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}
    </Typography>
  </Box>
);

const EmptyState: React.FC<{ 
  title: string; 
  description: string; 
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, description, icon, action }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      p: 4,
    }}
  >
    {icon && (
      <Box sx={{ mb: 2, color: 'text.secondary' }}>
        {icon}
      </Box>
    )}
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    {action}
  </Box>
);

export const ConversationView: React.FC<ConversationViewProps> = ({
  selectedChat,
  onSendMessage,
  onScheduleMessage,
  isSending,
  onMarkImportant,
  onMarkArchived,
  onMarkUnread,
  onSendEmailNotification,
  isAdmin = false,
  onChatUpdate,
  onSendScheduledNowSuccess,
  onSendScheduledNowError,
  owners = [],
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showScheduledView, setShowScheduledView] = useState(false);
  const [pendingSend, setPendingSend] = useState<InboxPendingSendState | null>(null);
  const [draftSuppressedAfterSend, setDraftSuppressedAfterSend] = useState(false);

  const onSendMessageRef = useRef(onSendMessage);
  useEffect(() => {
    onSendMessageRef.current = onSendMessage;
  }, [onSendMessage]);

  useEffect(() => {
    setPendingSend(null);
    setDraftSuppressedAfterSend(false);
  }, [selectedChat?.key]);

  useEffect(() => {
    if (pendingSend === null) return;

    if (pendingSend.countdown <= 0) {
      const { message, chatId, fromDraft } = pendingSend;
      onSendMessageRef.current(message, chatId);
      if (fromDraft) {
        setDraftSuppressedAfterSend(true);
      }
      setPendingSend(null);
      return;
    }

    const timer = setTimeout(() => {
      setPendingSend((prev) => (prev ? { ...prev, countdown: prev.countdown - 1 } : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [pendingSend]);

  const queueSend = useCallback(
    (message: string, chatId?: string, opts?: { fromDraft?: boolean }) => {
      if (isSending) return;
      setPendingSend((prev) => {
        if (prev !== null) return prev;
        return {
          message,
          countdown: INBOX_SEND_DELAY_SECONDS,
          chatId,
          fromDraft: opts?.fromDraft,
        };
      });
    },
    [isSending]
  );

  const cancelPendingSend = useCallback(() => {
    setPendingSend(null);
  }, []);

  const { data: scheduledMessages = [] } = useQuery({
    queryKey: ['scheduledMessages', selectedChat?.key],
    queryFn: () => (selectedChat ? chatApi.getScheduledMessages(selectedChat.key) : []),
    enabled: !!selectedChat?.key,
  });

  const deleteScheduledMutation = useMutation({
    mutationFn: ({ chatId, jobUuid }: { chatId: string; jobUuid: string }) =>
      chatApi.deleteScheduledMessage(chatId, jobUuid),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });

  const sendScheduledMessageNowMutation = useMutation({
    mutationFn: async ({
      chatId,
      jobUuid,
      text,
    }: {
      chatId: string;
      jobUuid: string;
      text: string;
    }) => {
      await chatApi.deleteScheduledMessage(chatId, jobUuid);
      return chatApi.sendReply(chatId, { content: text });
    },
    onSuccess: (updatedChat, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chat', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      onChatUpdate?.(updatedChat);
      onSendScheduledNowSuccess?.();
    },
    onError: (error) => {
      onSendScheduledNowError?.(error as Error);
    },
  });
  
  // Group messages by date
  const groupMessagesByDate = (messages: ConversationMessage[]) => {
    const groups: { [key: string]: ConversationMessage[] } = {};
    messages.forEach(message => {
      const date = new Date(message.received_date).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    
    // Sort messages within each day group by timestamp (oldest to newest)
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => 
        new Date(a.received_date).getTime() - new Date(b.received_date).getTime()
      );
    });
    
    return Object.entries(groups).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());
  };

  if (!selectedChat) {
    return (
      <EmptyState
        title="Select a conversation"
        description="Choose a message from the list to start chatting"
        icon={<EmailIcon sx={{ fontSize: 48 }} />}
      />
    );
  }

  const messages = selectedChat.messages?.items || [];
  const groupedMessages = groupMessagesByDate(messages);
  const currentChatId = selectedChat.key;
  const prospect = selectedChat.prospect;
  const prospectName =
    prospect?.full_name ||
    (prospect?.first_name && prospect?.last_name
      ? `${prospect.first_name} ${prospect.last_name}`
      : prospect?.first_name ||
        prospect?.last_name ||
        'Unknown');

  const resolveSenderLabel = (message: ConversationMessage): string => {
    const { sender } = message;

    // System/pipeline tags apply to both directions
    if (sender === 'pipeline') return 'Pipeline';
    if (sender === 'ai') return 'AI';
    if (sender === 'system') return 'System';

    if (!message.is_sender) return prospectName;

    // Outbound: identify the human sender by UUID
    if (!sender) return 'Unknown';
    if (sender === user?.key) return 'You';

    // Try chat owner first (already in memory, no extra request)
    const chatOwner = selectedChat.owner;
    if (chatOwner && chatOwner.key === sender) return chatOwner.name;

    // Try the operators list
    const match = owners.find((o) => o.uuid === sender);
    if (match) return match.name;

    return 'Unknown';
  };

  return (
    <Box sx={{ height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%', minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            src={prospect?.profile_image || undefined}
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
            onClick={() => prospect?.key && navigate(`/leads/${prospect.key}`)}
          >
            {prospectName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => prospect?.key && navigate(`/leads/${prospect.key}`)}
              >
                {prospectName}
              </Typography>
              {selectedChat.conversation_state && (
                <Chip
                  label={getCategoryLabel(selectedChat.conversation_state)}
                  size="small"
                  sx={{
                    bgcolor: getCategoryColor(selectedChat.conversation_state),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
              <Box>
                <ProjectStreamTag project={selectedChat.project} stream={selectedChat.stream} />
              </Box>
              <MessageCategoriesTags categories={selectedChat.message_categories} />
              <TagsDisplay
                tags={selectedChat.tags || []}
                chatId={selectedChat.key}
                isAdmin={isAdmin}
                onUpdate={(newTags) => {
                  if (onChatUpdate) {
                    onChatUpdate({ ...selectedChat, tags: newTags });
                  }
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isAdmin && (
              <Tooltip title="Send meeting notification email">
                <IconButton 
                  size="small"
                  onClick={onSendEmailNotification}
                >
                  <EmailNotificationIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={selectedChat.is_important ? "Remove from important" : "Mark as important"}>
              <IconButton 
                size="small"
                onClick={() => onMarkImportant?.(selectedChat.key, !selectedChat.is_important)}
              >
                {selectedChat.is_important ? (
                  <StarIcon color="primary" />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={selectedChat.is_archived ? "Remove from archive" : "Archive"}>
              <IconButton 
                size="small"
                onClick={() => onMarkArchived?.(selectedChat.key, !selectedChat.is_archived)}
              >
                <ArchiveIcon color={selectedChat.is_archived ? "primary" : "inherit"} />
              </IconButton>
            </Tooltip>
            <Tooltip title={selectedChat.direction === 'outbound' ? "Cannot mark as unread - you sent the last message" : "Mark as unread"}>
              <span>
                <IconButton 
                  size="small"
                  onClick={() => onMarkUnread?.(selectedChat.key)}
                  disabled={selectedChat.direction === 'outbound'}
                >
                  <MarkUnreadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Messages or Scheduled Messages */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        {showScheduledView ? (
          scheduledMessages.length === 0 ? (
            <EmptyState
              title="No scheduled messages"
              description="Schedule a message using the dropdown next to the send button"
              icon={<EmailIcon sx={{ fontSize: 48 }} />}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Messages scheduled to be sent
              </Typography>
              {scheduledMessages.map((sm: ScheduledMessage) => (
                <ScheduledMessageItem
                  key={sm.key}
                  item={sm}
                  chatId={selectedChat.key}
                  onDelete={() =>
                    deleteScheduledMutation.mutate({ chatId: selectedChat.key, jobUuid: sm.key })
                  }
                  onSendNow={() =>
                    sendScheduledMessageNowMutation.mutate({
                      chatId: selectedChat.key,
                      jobUuid: sm.key,
                      text: sm.text,
                    })
                  }
                  isDeleting={deleteScheduledMutation.isPending}
                  isSendingNow={sendScheduledMessageNowMutation.isPending}
                />
              ))}
            </Box>
          )
        ) : (
          <>
            {groupedMessages.length === 0 ? (
              <EmptyState
                title="No messages yet"
                description="Start the conversation by sending a message"
                icon={<EmailIcon sx={{ fontSize: 48 }} />}
              />
            ) : (
              groupedMessages.map(([date, messages]) => (
                <Box key={date}>
                  <DateSeparator date={date} />
                  {messages.map((message, index) => (
                    <MessageBubble
                      key={`${message.received_date}-${index}`}
                      message={message}
                      isOwn={message.is_sender}
                      senderLabel={resolveSenderLabel(message)}
                    />
                  ))}
                </Box>
              ))
            )}
            <DraftReplyBlock
              chat={selectedChat}
              onQueueSend={queueSend}
              sendPipelineActive={pendingSend !== null}
              isSending={isSending}
              draftSuppressedAfterSend={draftSuppressedAfterSend}
            />
          </>
        )}
      </Box>

      {/* Message Input */}
      <MessageInput
        pendingSend={pendingSend}
        onQueueSend={queueSend}
        onCancelPendingSend={cancelPendingSend}
        activeChatId={currentChatId}
        onScheduleMessage={onScheduleMessage}
        isSending={isSending}
        scheduledCount={scheduledMessages.length}
        failedScheduledCount={scheduledMessages.filter((s) => s.status === 'FAILED').length}
        onCalendarClick={() => setShowScheduledView((v) => !v)}
        isScheduledView={showScheduledView}
      />
    </Box>
  );
}; 